import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import Paging from "../../component/Paging";
import request from '../../../utils/Request.utils';
import { formatYmd } from '../../../utils/Common.utils';
import { ToastContainer, toast } from 'react-toastify';
import Modal from '../../component/Modal';
import DatePicker from '../../component/DatePicker';

const getTodayDateString = () => new Date().toISOString().slice(0, 10);

const MemberManage = ({ isLoggedIn }) => {
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currPage, setCurrPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // 검색
  const [searchQuery, setSearchQuery] = useState('');
  const [inputQuery, setInputQuery] = useState(''); // 입력 필드 상태
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [inputStartDate, setInputStartDate] = useState('');
  const [inputEndDate, setInputEndDate] = useState('');
  const [dateInputKey, setDateInputKey] = useState(0);
  const todayDate = getTodayDateString();

  const successHandler = useCallback((response) => {
    if (response.messageCode === 200) {
      setUsers(response.data.dataList);
      setTotalPages(response.data.tot_page);
      setTotalCnt(response.data.totalCnt);
      setLoading(false);
    } else {
      toast.error("회원 정보를 불러올 수 없습니다.");
      setLoading(false);
    }
  }, []);

  const errorHandler = useCallback((error) => {
    toast.error("데이터를 불러오는 중 오류가 발생했습니다.");
    setLoading(false);
  }, []);

  const fetchUsers = useCallback(() => {
    // 페이지를 옮기거나 다시 불러올 때는 표를 숨기지 않는다 — 숨겼다 다시 그리면 표·페이지 번호가 깜빡인다 (loading 은 첫 불러오기에만 true)
    const params = {
      curr_page: currPage,
      per_page: 10,
      sort_col: 'reg_dtm',
      sort_by: 'desc',
      user_stat: 'all',
      search_query: searchQuery,
      search_start_date: searchStartDate,
      search_end_date: searchEndDate,
    };
    request.get("/user/list", params, successHandler, errorHandler);
  }, [currPage, searchQuery, searchStartDate, searchEndDate, successHandler, errorHandler]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (page) => {
    setCurrPage(page);
  };

  const handleSearch = () => {
    if (inputStartDate && inputStartDate > todayDate) {
      toast.error("가입일 시작일은 오늘 이후로 선택할 수 없습니다.");
      return;
    }

    if (inputEndDate && inputEndDate > todayDate) {
      toast.error("가입일 종료일은 오늘 이후로 선택할 수 없습니다.");
      return;
    }

    if (inputStartDate && inputEndDate && inputStartDate > inputEndDate) {
      toast.error("가입일 종료일은 시작일보다 빠를 수 없습니다.");
      return;
    }

    setCurrPage(1);
    setSearchStartDate(inputStartDate);
    setSearchEndDate(inputEndDate);
    setSearchQuery(inputQuery);
  };

  const handleReset = () => {
    setInputQuery('');
    setSearchQuery('');
    setInputStartDate('');
    setInputEndDate('');
    setSearchStartDate('');
    setSearchEndDate('');
    setDateInputKey((prev) => prev + 1);
    setCurrPage(1);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prevSelectedUsers =>
      prevSelectedUsers.includes(userId)
        ? prevSelectedUsers.filter(id => id !== userId)
        : [...prevSelectedUsers, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.user_seq));
    }
  };

  const openModal = () => {
    if (selectedUsers.length === 0) {
      toast.error("삭제할 회원을 선택해 주세요.");
      return;
    }
    setModalIsOpen(true);
  };
  const closeModal = () => setModalIsOpen(false);

  const handleDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error("삭제할 회원을 선택해 주세요.");
      closeModal();
      return;
    }

    try {
      await request.del("/user/delete", { user_seqs: selectedUsers }, () => {
        toast.success("회원이 성공적으로 삭제되었습니다.");
        fetchUsers();
        setSelectedUsers([]);
      }, () => {
        toast.error("회원 삭제 중 오류가 발생했습니다.");
      });
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">회원 목록</h2>
      <div className="adm_com_pg_content">
        <div className="adm_com_board_list__top">
          <div className="adm_com_board_search">
            <div className="adm_com_input_wrap flex_row">
              <span className="tit">가입일 : </span>
              <DatePicker
                key={`start-date-${dateInputKey}`}
                value={inputStartDate}
                max={inputEndDate || todayDate}
                onChange={setInputStartDate}
              />
              <span className="divider"> ~ </span>
              <DatePicker
                key={`end-date-${dateInputKey}`}
                value={inputEndDate}
                min={inputStartDate || undefined}
                max={todayDate}
                onChange={setInputEndDate}
              />
            </div>
            <article className="search end">
              {/*}
              <select className="select">
                <option value="전체">전체</option>
              </select>
              */}
              <input
                type="search"
                value={inputQuery}
                onChange={(e) => setInputQuery(e.target.value)}
                placeholder="아이디, 휴대폰, 이메일 검색"
                className="input ws type_search"
              />
              <button className="adm_com_btn blue" onClick={handleSearch}>조회</button>
              <button className="adm_com_btn gray" onClick={handleReset}>초기화</button>
            </article>
          </div>
        </div>
        <div className="adm_com_btn_wrap">
          <button className={`adm_com_btn ${selectedUsers.length > 0 ? "blue" : "gray"}`} onClick={openModal} disabled={selectedUsers.length === 0}>선택삭제</button>
        </div>
        {loading ? null : (
          <table className="adm_com_board_list adm_member_manage_list">
            <colgroup>
              <col width="5%" />
              <col width="5%" />
              <col width="8%" />
              <col width="20%" />
              <col width="14%" />
              <col width="20%" />
              <col width="10%" />
              <col width="10%" />
              <col width="8%" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="check">
                    <input
                      type="checkbox"
                      id="chk01"
                      className="adm_com_chk"
                      onChange={handleSelectAll}
                      checked={users.length > 0 && selectedUsers.length === users.length}
                    />
                    <label htmlFor="chk01" />
                  </div>
                </th>
                <th>번호</th>
                <th>회원번호</th>
                <th>아이디</th>
                <th>휴대폰</th>
                <th>이메일</th>
                <th>가입일</th>
                <th>최근접속일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.user_seq}>
                  <td>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={`chk${index + 2}`}
                        className="adm_com_chk"
                        checked={selectedUsers.includes(user.user_seq)}
                        onChange={() => handleSelectUser(user.user_seq)}
                      />
                      <label htmlFor={`chk${index + 2}`} />
                    </div>
                  </td>
                  <td>{totalCnt - ((currPage - 1) * 10 + index)}</td>
                  <td>{user.user_seq}</td>
                  <td className="td_ellipsis" title={user.user_id}>{user.user_id}</td>
                  <td>{user.user_phone}</td>
                  <td className="td_ellipsis" title={user.user_email}>{user.user_email}</td>
                  <td>{formatYmd(user.reg_dtm)}</td>
                  <td>{formatYmd(user.last_login_dt)}</td>
                  <td>
                    <NavLink
                      to={`/Member/MemberManage/Detail/${user.user_seq}`}
                      className="adm_com_btn blue line"
                    >
                      상세
                    </NavLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paging
          currentPage={currPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
      <Modal
        open={modalIsOpen}
        close={closeModal}
        type="confirm"
        popSize="s"
        title="삭제 확인"
        description="선택된 회원을 삭제하시겠습니까?"
      >
        <div className="btn_container">
          <button className="adm_com_btn blue" onClick={handleDelete}>삭제</button>
          <button className="adm_com_btn gray" onClick={closeModal}>취소</button>
        </div>
      </Modal>
      <ToastContainer />
    </main>
  );
};

export default MemberManage;
